/**
 * Firebase Auth Utilities
 *
 * Provides smart token management helpers that avoid the performance cost of
 * unconditional `getIdToken(true)` calls (which the codebase was previously
 * doing on every Firestore read).
 *
 * Background
 * ──────────
 * Firebase ID tokens are valid for one hour.  Calling `getIdToken(true)` forces
 * a network round-trip to refresh the token even when it has 55 minutes of
 * validity remaining.  This adds measurable latency to every comment/reply read.
 *
 * The correct strategy is:
 *   1. Check when the current token expires.
 *   2. Only force-refresh if expiry is within TOKEN_REFRESH_THRESHOLD_MS.
 *   3. On any error during the check, fall back to a silent (non-forced) refresh
 *      so callers always get a usable token.
 */

import { auth } from '../firebaseConfig';

/**
 * How close to expiry (in milliseconds) before we proactively force-refresh.
 * 5 minutes gives enough buffer to complete any in-flight requests.
 */
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Ensures the current user has a valid, non-expired Firebase ID token.
 *
 * - If the token has > TOKEN_REFRESH_THRESHOLD_MS left: does nothing (fast path).
 * - If the token is within 5 minutes of expiry:         force-refreshes via the network.
 * - If the token expiry cannot be determined:           attempts a silent refresh.
 * - If the user is not signed in:                       returns immediately (no-op).
 *
 * This replaces the pattern:
 *   `try { await auth?.currentUser?.getIdToken(true); } catch {}`
 *
 * which unconditionally triggered a network call on every invocation.
 *
 * @returns {Promise<void>}
 */
export async function ensureFreshToken() {
  const user = auth?.currentUser;
  if (!user) return;

  try {
    // getIdTokenResult() reads from the local token without a network call.
    const tokenResult = await user.getIdTokenResult(/* forceRefresh= */ false);
    const expiresAtMs = new Date(tokenResult.expirationTime).getTime();
    const msUntilExpiry = expiresAtMs - Date.now();

    if (msUntilExpiry < TOKEN_REFRESH_THRESHOLD_MS) {
      // Token is about to expire — refresh it now while we still have time.
      await user.getIdToken(/* forceRefresh= */ true);
    }
    // Otherwise the token is fresh — nothing to do.
  } catch {
    // If we couldn't read the expiry (e.g. no cached token), attempt a silent
    // refresh.  We deliberately do NOT force-refresh here to avoid a redundant
    // network round-trip when the token is actually still valid but the SDK
    // cache is cold.
    try {
      await user.getIdToken(/* forceRefresh= */ false);
    } catch {
      // Swallow — if the token is truly invalid the subsequent Firestore call
      // will fail with `permission-denied` and be handled by the caller.
    }
  }
}

/**
 * Returns the current user's UID, or null if not signed in.
 * Convenience wrapper used alongside ensureFreshToken().
 *
 * @returns {string | null}
 */
export function getCurrentUid() {
  return auth?.currentUser?.uid ?? null;
}
